import {
  AllocationUtilizationParams,
  getAllocationUtilization,
  listAllocationUtilizationPeriods,
  PointsPeriod,
} from '@kudos/http-client'
import { useSearchState } from '@shared/search/searchState/useSearchState'
import { firstValue, SearchState } from '@shared/search/state'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { AllocationUtilizationFilters, resolveSelectedPeriod } from './allocationUtilizationUtils'

export const ALLOCATION_UTILIZATION_PERIODS_QK = 'allocationUtilizationPeriods'
export const ALLOCATION_UTILIZATION_QK = 'allocationUtilization'

// The default period depends on the periods response, so it isn't seeded here.
const PERIOD_SEARCH_DEFAULTS: SearchState = {}
// Every filter defaults to unset, which reports on all members.
const FILTER_SEARCH_DEFAULTS: SearchState = {}

export const allocationUtilizationQueryKey = (params: AllocationUtilizationParams | undefined) =>
  [ALLOCATION_UTILIZATION_QK, params] as const

export const useAllocationUtilizationPeriods = () =>
  useQuery({
    queryKey: [ALLOCATION_UTILIZATION_PERIODS_QK],
    queryFn: listAllocationUtilizationPeriods,
  })

/**
 * The period list and the selected period. The selection lives in the URL with
 * the report's other filters, so it survives a trip to Share and back.
 */
export const useAllocationUtilizationPeriod = () => {
  const { data, isPending } = useAllocationUtilizationPeriods()
  const [searchState, updateSearchState] = useSearchState(PERIOD_SEARCH_DEFAULTS, true)
  const periods = data?.results
  const persistedPeriod = firstValue(searchState.period)

  const period = useMemo(
    () => resolveSelectedPeriod(periods, persistedPeriod),
    [periods, persistedPeriod],
  )

  const setPeriod = useCallback(
    (value: PointsPeriod) => updateSearchState({ period: [value] }),
    [updateSearchState],
  )

  return { periods, period, setPeriod, isPending }
}

/** The filters drawer state, kept in the URL alongside the period. */
export const useAllocationUtilizationFilters = () => {
  const [searchState, updateSearchState] = useSearchState(FILTER_SEARCH_DEFAULTS, true)
  const filters: AllocationUtilizationFilters = searchState

  const setFilters = useCallback(
    (updates: AllocationUtilizationFilters) => updateSearchState(updates),
    [updateSearchState],
  )

  return { filters, setFilters }
}

export const useAllocationUtilization = (params: AllocationUtilizationParams | undefined) =>
  useQuery({
    queryKey: allocationUtilizationQueryKey(params),
    queryFn: () => {
      if (!params) throw new Error('Allocation utilization requires a period')
      return getAllocationUtilization(params)
    },
    enabled: !!params?.period,
  })
